import { z } from 'zod'
import type { WorkbookBlock } from './workbooks'

/**
 * Workbook block specs — the typed, editable view of each notebook block kind.
 *
 * Shapes are from the 2026-09-28 capture (docs/inventory/workbook-blocks-capture.md,
 * 2,000 workbooks, every kind present). A block stays a raw-preserving
 * `WorkbookBlock`; a spec is read FROM `raw` and written back ONTO it, so keys this
 * client does not model survive an edit byte for byte.
 *
 * Kind vs wire type: the Search block is a `dql` block carrying `block: "GUI"`;
 * every other kind is its wire `type`.
 */

export const BLOCK_KINDS = [
  'dql', 'gui', 'sql', 'text', 'code', 'ai', 'visual', 'signal', 'call', 'return', 'notification',
] as const
export type BlockKind = (typeof BLOCK_KINDS)[number]

export function blockKind(b: WorkbookBlock): BlockKind | null {
  if (b.type === 'dql') return b.gui ? 'gui' : 'dql'
  return (BLOCK_KINDS as readonly string[]).includes(b.type) ? (b.type as BlockKind) : null
}

/** The wire block a new block of this kind starts from (legacy editor defaults). */
export function newBlockRaw(kind: BlockKind): Record<string, unknown> {
  switch (kind) {
    case 'dql':
    case 'sql':
      return { type: kind, query: '' }
    case 'gui':
      return { type: 'dql', block: 'GUI', query: '', hasSourceName: false }
    case 'text':
      return { type: 'text', code: '' }
    case 'code':
      return { type: 'code', code: '', language: 'python-3.8', encoding: 'bytecode' }
    case 'ai':
      return {
        type: 'ai',
        data: {
          contamination_factor: 0.001,
          query_data: { stream: '', entity: '', duration: '1d', feature_list: [], filter_list: [] },
          result_condition: { only_anomaly: true, return_graph_result: true, feature_contribution: false },
        },
      }
    case 'visual':
      return {
        type: 'visual',
        data: { chart_type: 'bar', widget_name: '', config: { type: 'default', key: [null, null, null], value: '' } },
      }
    case 'signal':
      return { type: 'signal' }
    case 'call':
    case 'return':
      return { type: kind, name: '', path: '', query: '' }
    case 'notification':
      return {
        type: 'notification',
        email_list: [], email_cc_list: [], email_bcc_list: [],
        email_subject: '', email_body: '', selectedColumns: [],
        sendMailForAll: false, sendMailForAllColumns: false, sendMailForAllRows: false, topRows: '',
      }
  }
}

/* ------------------------------------------------------------------ base64 */

/** UTF-8 safe base64 (code blocks carry python source as base64). */
export function decodeBlockSource(b64: string): string {
  if (!b64) return ''
  try {
    const bin = atob(b64)
    return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
  } catch {
    return b64 // not base64 — show what is stored rather than nothing
  }
}

export function encodeBlockSource(text: string): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(text)))
}

/* ------------------------------------------------------------------ schemas */

const str = z.preprocess((v) => (typeof v === 'number' ? String(v) : v), z.string()).catch('')
const optStr = z.string().optional().catch(undefined)
const bool = z.boolean().catch(false)
const strList = z.array(z.string()).catch([])
/** A nested object that is absent or malformed reads as `{}`; every field below
 * carries its own fallback, so a reader never throws on user content. */
const obj = <T extends z.ZodRawShape>(shape: T) =>
  z.preprocess((v) => (typeof v === 'object' && v !== null && !Array.isArray(v) ? v : {}), z.object(shape).passthrough())

export const queryBlockSchema = obj({ query: str })
export const textBlockSchema = obj({ code: str })
export const codeBlockSchema = obj({ code: str, language: str })
export const callBlockSchema = obj({ name: str, path: str, query: str })

const featureEntry = z.record(z.union([z.string(), z.array(z.string())]))
export const outlierBlockSchema = obj({
  data: obj({
    contamination_factor: z.number().catch(0.001),
    query_data: obj({
      stream: str,
      entity: str,
      duration: optStr,
      start_time: optStr,
      end_time: optStr,
      feature_list: z.array(featureEntry).catch([]),
      filter_list: z.array(obj({ field: str, operator: str, value: str })).catch([]),
    }),
    result_condition: obj({ only_anomaly: bool, return_graph_result: bool, feature_contribution: bool }),
  }),
})

export const visualBlockSchema = obj({
  data: obj({
    chart_type: str,
    widget_name: str,
    config: obj({ type: str, key: z.array(z.string().nullable()).catch([]), value: str, dataframe: optStr }),
  }),
})

export const notificationBlockSchema = obj({
  email_list: strList,
  email_cc_list: strList,
  email_bcc_list: strList,
  email_subject: str,
  email_body: str,
  selectedColumns: strList,
  sendMailForAll: bool,
  sendMailForAllColumns: bool,
  sendMailForAllRows: bool,
  topRows: str,
})

/* ------------------------------------------------------------------ specs */

export interface QuerySpec { query: string }
export interface TextSpec { text: string }
export interface CodeSpec { source: string; language: string }
export interface CallSpec {
  /** The target workbook's id — stored in the wire `query` key. */
  workbookId: string
  name: string
  path: string
}
export interface OutlierFeature {
  fn: string
  field: string
  /** count_if / percentage_of carry a second operand. */
  value: string | null
}
export interface OutlierFilter { field: string; operator: string; value: string }
export interface OutlierSpec {
  stream: string
  entity: string
  /** Relative window (e.g. `1d`); null when an absolute start/end is stored. */
  duration: string | null
  startTime: string | null
  endTime: string | null
  features: OutlierFeature[]
  filters: OutlierFilter[]
  contamination: number
  onlyAnomaly: boolean
  graphResult: boolean
  featureContribution: boolean
}
export interface VisualSpec {
  chartType: string
  variant: string
  keys: (string | null)[]
  value: string
  dataframe: string | null
  widgetName: string
}
export interface NotificationSpec {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  body: string
  columns: string[]
  forAll: boolean
  allColumns: boolean
  allRows: boolean
  topRows: string
}

/** Feature functions the outlier engine takes (type file `AIBlockFeatureKey`). */
export const OUTLIER_FUNCTIONS = ['distinct', 'count', 'sum', 'avg', 'min', 'max', 'count_if', 'percentage_of'] as const
export const OUTLIER_TWO_OPERAND = new Set(['count_if', 'percentage_of'])

/* ------------------------------------------------------------------ readers */

export function readQuerySpec(b: WorkbookBlock): QuerySpec {
  return { query: queryBlockSchema.parse(b.raw).query }
}
export function readTextSpec(b: WorkbookBlock): TextSpec {
  return { text: textBlockSchema.parse(b.raw).code }
}
export function readCodeSpec(b: WorkbookBlock): CodeSpec {
  const r = codeBlockSchema.parse(b.raw)
  return { source: decodeBlockSource(r.code), language: r.language || 'python-3.8' }
}
export function readCallSpec(b: WorkbookBlock): CallSpec {
  const r = callBlockSchema.parse(b.raw)
  return { workbookId: r.query, name: r.name, path: r.path }
}
export function readOutlierSpec(b: WorkbookBlock): OutlierSpec {
  const d = outlierBlockSchema.parse(b.raw).data
  const q = d.query_data
  const rc = d.result_condition
  return {
    stream: q.stream,
    entity: q.entity,
    duration: q.duration ?? (q.start_time ? null : ''),
    startTime: q.start_time ?? null,
    endTime: q.end_time ?? null,
    features: q.feature_list.flatMap((f) => {
      const [fn, v] = Object.entries(f)[0] ?? []
      if (!fn || v === undefined) return []
      return [{ fn, field: Array.isArray(v) ? (v[0] ?? '') : v, value: Array.isArray(v) ? (v[1] ?? '') : null }]
    }),
    filters: q.filter_list.map((f) => ({ field: f.field, operator: f.operator, value: f.value })),
    contamination: d.contamination_factor,
    onlyAnomaly: rc.only_anomaly,
    graphResult: rc.return_graph_result,
    featureContribution: rc.feature_contribution,
  }
}
export function readVisualSpec(b: WorkbookBlock): VisualSpec {
  const d = visualBlockSchema.parse(b.raw).data
  return {
    chartType: d.chart_type,
    variant: d.config.type,
    keys: d.config.key,
    value: d.config.value,
    dataframe: d.config.dataframe ?? null,
    widgetName: d.widget_name,
  }
}
export function readNotificationSpec(b: WorkbookBlock): NotificationSpec {
  const r = notificationBlockSchema.parse(b.raw)
  return {
    to: r.email_list, cc: r.email_cc_list, bcc: r.email_bcc_list,
    subject: r.email_subject, body: r.email_body, columns: r.selectedColumns,
    forAll: r.sendMailForAll, allColumns: r.sendMailForAllColumns, allRows: r.sendMailForAllRows,
    topRows: r.topRows,
  }
}

/* ------------------------------------------------------------------ writers */

/**
 * Each writer returns a NEW block whose raw is the old raw with only this
 * spec's keys replaced. The typed mirror fields (`query`, `code`, `name`,
 * `path`) are kept in step so the existing serializer (which rebuilds dql /
 * sql / text / code from them) writes the same thing.
 */
function withRaw(b: WorkbookBlock, raw: Record<string, unknown>): WorkbookBlock {
  return {
    ...b,
    raw,
    query: typeof raw.query === 'string' ? raw.query : b.query,
    code: typeof raw.code === 'string' ? raw.code : b.code,
    name: typeof raw.name === 'string' ? raw.name : b.name,
    path: typeof raw.path === 'string' ? raw.path : b.path,
  }
}

export function writeQuerySpec(b: WorkbookBlock, s: QuerySpec): WorkbookBlock {
  return withRaw(b, { ...b.raw, type: b.type, query: s.query })
}
export function writeTextSpec(b: WorkbookBlock, s: TextSpec): WorkbookBlock {
  return withRaw(b, { ...b.raw, type: 'text', code: s.text })
}
export function writeCodeSpec(b: WorkbookBlock, s: CodeSpec): WorkbookBlock {
  return withRaw(b, {
    encoding: 'bytecode',
    ...b.raw,
    type: 'code',
    language: s.language || 'python-3.8',
    code: encodeBlockSource(s.source),
  })
}
export function writeCallSpec(b: WorkbookBlock, s: CallSpec): WorkbookBlock {
  return withRaw(b, { ...b.raw, type: b.type, query: s.workbookId, name: s.name, path: s.path })
}
export function writeOutlierSpec(b: WorkbookBlock, s: OutlierSpec): WorkbookBlock {
  const old = (b.raw.data ?? {}) as Record<string, unknown>
  const oldQ = (old.query_data ?? {}) as Record<string, unknown>
  const oldRc = (old.result_condition ?? {}) as Record<string, unknown>
  const { duration: _d, start_time: _s, end_time: _e, ...restQ } = oldQ
  const window =
    s.duration !== null ? { duration: s.duration } : { start_time: s.startTime ?? '', end_time: s.endTime ?? '' }
  return withRaw(b, {
    ...b.raw,
    type: 'ai',
    data: {
      ...old,
      contamination_factor: s.contamination,
      query_data: {
        ...restQ,
        stream: s.stream,
        entity: s.entity,
        ...window,
        feature_list: s.features.map((f) =>
          OUTLIER_TWO_OPERAND.has(f.fn) ? { [f.fn]: [f.field, f.value ?? ''] } : { [f.fn]: f.field },
        ),
        filter_list: s.filters.map((f) => ({ field: f.field, operator: f.operator, value: f.value })),
      },
      result_condition: {
        ...oldRc,
        only_anomaly: s.onlyAnomaly,
        return_graph_result: s.graphResult,
        // Absent on 8 of 29 captured blocks: only write it when it was there or is set.
        ...('feature_contribution' in oldRc || s.featureContribution
          ? { feature_contribution: s.featureContribution }
          : {}),
      },
    },
  })
}
export function writeVisualSpec(b: WorkbookBlock, s: VisualSpec): WorkbookBlock {
  const old = (b.raw.data ?? {}) as Record<string, unknown>
  const oldC = (old.config ?? {}) as Record<string, unknown>
  const { dataframe: _df, ...restC } = oldC
  return withRaw(b, {
    ...b.raw,
    type: 'visual',
    data: {
      ...old,
      chart_type: s.chartType,
      widget_name: s.widgetName,
      config: {
        ...restC,
        type: s.variant,
        key: s.keys,
        value: s.value,
        ...(s.dataframe !== null ? { dataframe: s.dataframe } : {}),
      },
    },
  })
}
export function writeNotificationSpec(b: WorkbookBlock, s: NotificationSpec): WorkbookBlock {
  return withRaw(b, {
    ...b.raw,
    type: 'notification',
    email_list: s.to, email_cc_list: s.cc, email_bcc_list: s.bcc,
    email_subject: s.subject, email_body: s.body, selectedColumns: s.columns,
    sendMailForAll: s.forAll, sendMailForAllColumns: s.allColumns, sendMailForAllRows: s.allRows,
    topRows: s.topRows,
  })
}

/* ------------------------------------------------------------ notification */

/**
 * The legacy submit rules for a Notification block (backend owner, 2026-09-28;
 * legacy email.js). The frontend only collects and validates these; the flags
 * mean something only in consolidated mode (`sendMailForAll`).
 */
export const NOTIFICATION_TOP_ROWS_MAX = 10_000

/** Why this block cannot be saved yet; empty = fine. */
export function notificationProblems(s: NotificationSpec): string[] {
  if (!s.forAll) return []
  const problems: string[] = []
  if (!s.allColumns && s.columns.length === 0) problems.push('choose "all columns" or at least one column')
  const top = Number(s.topRows)
  if (!s.allRows && !(/^\d+$/.test(s.topRows) && top >= 1 && top <= NOTIFICATION_TOP_ROWS_MAX)) {
    problems.push(`choose "all rows" or top rows between 1 and ${NOTIFICATION_TOP_ROWS_MAX.toLocaleString('en')}`)
  }
  return problems
}

/**
 * The block as it is saved: with `sendMailForAll` off, the three consolidated
 * fields and the column list are forced empty (legacy payload rule). Applied at
 * save, not while editing, so toggling the mode back keeps what was chosen.
 */
export function notificationForSave(b: WorkbookBlock): WorkbookBlock {
  if (b.type !== 'notification') return b
  const s = readNotificationSpec(b)
  if (s.forAll) return b
  return writeNotificationSpec(b, { ...s, allRows: false, allColumns: false, columns: [], topRows: '' })
}

/* ------------------------------------------------------------------ visual */

/** Chart types and variants seen in the capture (`chart_type` × `config.type`). */
export const VISUAL_CHARTS: Record<string, string[]> = {
  bar: ['default', 'stacked', 'group'],
  column: ['default', 'stacked', 'group'],
  area: ['default'],
  line: ['default'],
  pie: ['default', 'donut'],
  radial: ['default', 'stacked'],
  bubble: ['default', 'stacked'],
  sankey: ['default'],
  metric: ['default'],
  bipartiteChord: ['default'],
  timeline: ['default', 'stacked'],
  timebar: ['default'],
  geoMap: ['default'],
  wordCloud: ['default'],
}

/** Signal entity keys — the fixed twelve (every key seen in the capture is one). */
export const SIGNAL_ENTITY_KEYS = [
  'SuspectHost', 'SuspectUser', 'SuspectObject', 'SuspectProcess', 'SuspectHash', 'SuspectUrl',
  'SuspectAction', 'SuspectLocation', 'TargetHost', 'TargetUser', 'TargetResource', 'TargetPort',
] as const
